import { nextTick, onBeforeUnmount, watch } from "vue";

const RETRY_MS = 50;
const MAX_WAIT_MS = 4000;

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeQueryParam(value) {
	if (value == null || value === "") return "";
	return String(Array.isArray(value) ? value[0] : value);
}

/**
 * @param {Element|import('vue').Ref|null|undefined} el
 * @returns {Element|null}
 */
function unwrapEl(el) {
	const node = el && typeof el === "object" && "value" in el ? el.value : el;
	return node instanceof Element ? node : null;
}

function prefersReducedMotion() {
	if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
		return false;
	}
	return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * @param {() => Element|import('vue').Ref|null|undefined} getEl
 * @param {number} [timeout]
 * @returns {Promise<Element|null>}
 */
function waitForElement(getEl, timeout = MAX_WAIT_MS) {
	return new Promise(resolve => {
		const startedAt = Date.now();

		const tryFind = () => {
			const node = unwrapEl(getEl());
			if (node?.isConnected) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => resolve(node));
				});
				return;
			}
			if (Date.now() - startedAt >= timeout) {
				resolve(null);
				return;
			}
			setTimeout(tryFind, RETRY_MS);
		};

		nextTick(tryFind);
	});
}

/**
 * @param {Element} node
 * @param {{ offset?: number }} [options]
 */
function scrollNodeIntoView(node, { offset = 0 } = {}) {
	if (typeof window === "undefined") return;

	const behavior = prefersReducedMotion() ? "auto" : "smooth";
	const top = Math.max(0, node.getBoundingClientRect().top + window.scrollY - offset);

	try {
		window.scrollTo({ top, behavior });
		return;
	} catch {
		// ignore and fall back
	}

	try {
		node.scrollIntoView({ behavior, block: "start" });
	} catch {
		try {
			node.scrollIntoView(true);
		} catch {
			window.scrollTo(0, top);
		}
	}
}

/**
 * 关于我们页锚点滚动：等待目标就绪、监听 query 变化、滚动 API 降级。
 * @param {import('vue').Ref<Record<string, import('vue').Ref|Element|null>>|Record<string, import('vue').Ref|Element|null>} elMap
 * @param {{ offset?: number }} [options]
 */
export function useAboutUsAnchorScroll(elMap, options = {}) {
	const route = useRoute();
	const offset = options.offset ?? 0;
	let disposed = false;
	let scrollToken = 0;

	onBeforeUnmount(() => {
		disposed = true;
		scrollToken += 1;
	});

	const resolveMapTarget = key => {
		const map = elMap?.value ?? elMap;
		return map?.[key] ?? null;
	};

	/**
	 * @param {string} key
	 * @param {() => void} [onAfterScroll]
	 */
	const scrollToKey = async (key, onAfterScroll) => {
		if (!import.meta.client || !key) return;

		const token = ++scrollToken;
		const node = await waitForElement(() => resolveMapTarget(key));
		if (disposed || token !== scrollToken || !node) return;

		scrollNodeIntoView(node, { offset });

		if (typeof onAfterScroll === "function") {
			const delay = prefersReducedMotion() ? 0 : 600;
			setTimeout(() => {
				if (!disposed && token === scrollToken) onAfterScroll();
			}, delay);
		}
	};

	/**
	 * @param {Element|import('vue').Ref|null|undefined} el
	 * @param {() => void} [onAfterScroll]
	 */
	const scrollToEl = async (el, onAfterScroll) => {
		if (!import.meta.client) return;

		const token = ++scrollToken;
		const node = unwrapEl(el) ?? (await waitForElement(() => el));
		if (disposed || token !== scrollToken || !node) return;

		scrollNodeIntoView(node, { offset });

		if (typeof onAfterScroll === "function") {
			const delay = prefersReducedMotion() ? 0 : 600;
			setTimeout(() => {
				if (!disposed && token === scrollToken) onAfterScroll();
			}, delay);
		}
	};

	const scrollToTarget = () => {
		const to = normalizeQueryParam(route.query.to);
		if (to) scrollToKey(to);
	};

	watch(
		() => normalizeQueryParam(route.query.to),
		(to, prev) => {
			if (to && to !== prev) scrollToKey(to);
		}
	);

	return {
		scrollToTarget,
		scrollToKey,
		scrollToEl,
		normalizeQueryParam,
	};
}
